import { Immutable } from "immer";
import React, { useEffect, useRef, useState } from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { IReviewsProductTypeID } from "../../../models/nominals";
import {
    IReviewQuery,
    ISearchFacet,
    ISearchReviewArgs,
    ReviewSortOption,
} from "../../../models/reviews";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { serializeFacetValues } from "../../../utils/reviews";
import {
    getInitialFilterStateFromURL,
    getParamsFromFacetValues,
    updateQueryParams,
} from "../history";
import {
    setFacetValues,
    setProductTypes,
    toggleMobileSideNav,
} from "../reducers";
import type { IReviewsAppOwnProps } from "../reviewsApp.interfaces";
import { loadReviews } from "../thunks";
import { ReviewsViewer as ReviewsViewerComponent } from "./ReviewsViewerContent";

type IProps = Immutable<
    IReviewsAppOwnProps & {
        showSources?: boolean;
        // Defaults to showSources: the standalone iframe widget turns the
        // filter and the links off together. Stearns passes them separately
        // so it can hide the filter and keep the links (#30638).
        enablePDPLinks?: boolean;
        writeReviewURL?: string;
        showAppliedFilters: boolean;
        isCondensedView?: boolean;
    }
>;

const buildReviewSearchQuery = (args: {
    brandID: IProps["brandID"];
    pageSize: IProps["pageSize"];
    displayedProductTypeIDWhitelist: IProps["displayedProductTypeIDWhitelist"];
    productTypeIDs: readonly IReviewsProductTypeID[];
    selectedSortOption: ReviewSortOption;
    facetValues: IReviewQuery;
    page: number;
}): ISearchReviewArgs => {
    let productTypeIDs = args.productTypeIDs;
    if (
        productTypeIDs.length <= 0 &&
        args.displayedProductTypeIDWhitelist &&
        args.displayedProductTypeIDWhitelist.length > 0
    ) {
        productTypeIDs = args.displayedProductTypeIDWhitelist;
    }
    const productTypeIDString = serializeFacetValues(productTypeIDs);
    const { product_type_id: _, ...facetValuesWithoutProductType } =
        args.facetValues;
    const query: ISearchReviewArgs = {
        brand_id: args.brandID,
        ordering: args.selectedSortOption,
        product_type_id: productTypeIDString ? productTypeIDString : undefined,
        page: args.page,
        page_size: args.pageSize,
        ...facetValuesWithoutProductType,
    };
    return query;
};

const listReviewsAsync = async (
    args: Parameters<typeof buildReviewSearchQuery>[0] & {
        dispatch: ReturnType<typeof useAppDispatch>;
    },
): Promise<void> => {
    const query = buildReviewSearchQuery(args);
    await args.dispatch(
        loadReviews({
            brandID: args.brandID,
            query,
        }),
    );
};

const initSourceFilter = (args: {
    sourceFilterWhitelist: IProps["sourceFilterWhitelist"];
    facetValues: IReviewQuery;
    facets: ReadonlyArray<ISearchFacet>;
    dispatch: ReturnType<typeof useAppDispatch>;
}): void => {
    const sourceFilterWhitelist = args.sourceFilterWhitelist;
    if (!sourceFilterWhitelist || sourceFilterWhitelist.length <= 0) {
        return;
    }
    const sourceFacet = args.facets.find((facet) => {
        return facet.type === "source_id";
    });
    if (!sourceFacet) {
        return;
    }
    const optionIDs = sourceFacet.options
        .filter((option): boolean => {
            return sourceFilterWhitelist.includes(`${option.name}`);
        })
        .map((option): number => {
            return option.option_id;
        });
    const sourceIDs = serializeFacetValues(optionIDs);
    if (args.facetValues.source_id !== sourceIDs) {
        args.dispatch(
            setFacetValues({
                ...args.facetValues,
                source_id: sourceIDs,
            }),
        );
    }
};

export const ReviewsViewer = (props: IProps): React.ReactNode => {
    const {
        brandID,
        pageSize = 6,
        displayedProductTypeIDWhitelist,
        sourceFilterWhitelist,
        starHasStroke,
        showSources = true,
        enablePDPLinks,
        writeReviewURL = "",
        showAppliedFilters,
        isCondensedView,
    } = props;

    const state = useAppSelector((rootState) => rootState.reviews);
    const dispatch = useAppDispatch();

    // Capture the URL-derived filter state during render, BEFORE any effect
    // runs. The "update query string" effect below rewrites window.location
    // from the current (SSR-prefilled) state on mount, which would clobber the
    // filter params we need to read here. Capturing lazily during render
    // freezes the original URL filters.
    const [initialURLFilterState] = useState(getInitialFilterStateFromURL);

    const hasMounted = useRef(false);
    const listReviewsTimerRef = useRef<number | undefined>(undefined);

    const listReviewsDebounced = (page: number, delayMs = 100) => {
        const window = getWindow();
        if (!window) {
            return;
        }
        if (listReviewsTimerRef.current) {
            window.clearTimeout(listReviewsTimerRef.current);
        }
        listReviewsTimerRef.current = window.setTimeout(() => {
            void listReviewsAsync({
                brandID,
                pageSize,
                displayedProductTypeIDWhitelist,
                productTypeIDs: state.ui.productTypeIDs,
                selectedSortOption: state.ui.selectedSortOption,
                facetValues: state.ui.facetValues,
                page,
                dispatch,
            });
        }, delayMs);
    };

    const onLoadMore = async (): Promise<void> => {
        await listReviewsAsync({
            brandID,
            pageSize,
            displayedProductTypeIDWhitelist,
            productTypeIDs: state.ui.productTypeIDs,
            selectedSortOption: state.ui.selectedSortOption,
            facetValues: state.ui.facetValues,
            page: state.ui.page + 1,
            dispatch,
        });
    };

    const onToggleMobileSideNav = () => {
        dispatch(toggleMobileSideNav());
    };

    // Update source filter when sourceFilterWhitelist or facets change
    useEffect(() => {
        initSourceFilter({
            sourceFilterWhitelist,
            facetValues: state.ui.facetValues,
            facets: state.data.facets,
            dispatch,
        });
    }, [
        sourceFilterWhitelist,
        state.data.facets,
        state.ui.facetValues,
        dispatch,
    ]);

    // Reload reviews when sort, filters, or product types change.
    // Skip the initial mount — SSR data is already in the store.
    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            return;
        }
        listReviewsDebounced(1);
    }, [
        state.ui.selectedSortOption,
        state.ui.productTypeIDs,
        state.ui.facetValues,
    ]);

    // Apply the URL-derived filter state on hydration. In deployed
    // environments a CDN strips the reviews filter params before the request
    // reaches Django, so the SSR-rendered reviews are unfiltered even though
    // the browser URL still carries the filters. Dispatching here applies the
    // URL filters to the store; because this runs after the reload effect
    // above has already set hasMounted, the resulting state change triggers a
    // filtered reviews fetch. Gated on non-empty so plain reviews pages (no
    // URL filters) stay a no-op and don't refetch.
    useEffect(() => {
        if (initialURLFilterState.productTypeIDs.length > 0) {
            dispatch(setProductTypes(initialURLFilterState.productTypeIDs));
        }
        // Old links can still carry ?source_id=... and with the Sources
        // section hidden nothing can unset it, so drop it here too. The server
        // leaves it out of its own query for the same reason.
        const { source_id: _, ...withoutSource } =
            initialURLFilterState.facetValues;
        const urlFacetValues = showSources
            ? initialURLFilterState.facetValues
            : withoutSource;
        if (Object.keys(initialURLFilterState.facetValues).length > 0) {
            dispatch(setFacetValues(urlFacetValues));
        }
        // Run once on mount; initialURLFilterState is captured during render
        // and never changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update query string to match current facet selections
    useEffect(() => {
        const params = getParamsFromFacetValues(
            state.ui.facetValues,
            state.ui.productTypeIDs,
        );
        updateQueryParams(params);
    }, [state.ui.facetValues, state.ui.productTypeIDs]);

    const showSourcesValue = !!showSources;
    const enablePDPLinksValue = enablePDPLinks ?? showSourcesValue;
    return (
        <ReviewsViewerComponent
            displayedProductTypeIDWhitelist={
                displayedProductTypeIDWhitelist || []
            }
            enablePDPLinks={enablePDPLinksValue}
            isCondensedView={isCondensedView}
            mobileSideNavOpen={state.ui.mobileSideNavOpen}
            onLoadMore={onLoadMore}
            onToggleMobileSideNav={onToggleMobileSideNav}
            showAppliedFilters={showAppliedFilters}
            showSources={showSourcesValue}
            starHasStroke={starHasStroke}
            writeReviewURL={writeReviewURL}
        />
    );
};
